import { MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

// ============================================
// WHATSAPP CONFIGURATION - UPDATE YOUR NUMBER HERE
// ============================================
const WHATSAPP_CONFIG = {
  // Main business WhatsApp number (update this with your actual number)
  BUSINESS_NUMBER: '+27615304587',
  
  // Department tags for message routing
  TAGS: {
    SUPPORT: '[SUPPORT]',
    ORDER: '[ORDER]',
    DELIVERY: '[DELIVERY]',
    DRIVER: '[DRIVER]',
    REFUND: '[REFUND]'
  }
};

// Export for easy access
export const SUPPORT_WHATSAPP = WHATSAPP_CONFIG.BUSINESS_NUMBER;

/**
 * Generate WhatsApp click-to-chat URL
 * @param {string} phone - Phone number with country code (e.g., +27123456789)
 * @param {string} message - Pre-filled message
 * @returns {string} WhatsApp URL
 */
export const getWhatsAppUrl = (phone, message = '') => {
  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
};

/**
 * WhatsApp button component for various use cases
 */
export const WhatsAppButton = ({ 
  phone = SUPPORT_WHATSAPP, 
  message = '', 
  label = 'Chat on WhatsApp',
  variant = 'default',
  size = 'default',
  className = '',
  showIcon = true
}) => {
  const handleClick = () => {
    window.open(getWhatsAppUrl(phone, message), '_blank');
  };

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      size={size}
      className={`bg-green-600 hover:bg-green-700 text-white ${className}`}
      data-testid="whatsapp-button"
    >
      {showIcon && <MessageCircle className="w-5 h-5 mr-2" />}
      {label}
    </Button>
  );
};

/**
 * Pre-built message templates with department tags for easy routing
 */
export const WhatsAppMessages = {
  // ============ CUSTOMER SUPPORT [SUPPORT] ============
  generalSupport: () => 
    `${WHATSAPP_CONFIG.TAGS.SUPPORT} Hi NarkOnline! 👋\n\nI need help with something.\n\n`,
  
  becomeDriver: () => 
    `${WHATSAPP_CONFIG.TAGS.SUPPORT} Hi NarkOnline! 🛵\n\nI'm interested in becoming a delivery driver.\n\nPlease share the requirements. Thank you!`,

  // ============ ORDERS & SALES [ORDER] ============
  orderConfirmation: (orderId, total) => 
    `${WHATSAPP_CONFIG.TAGS.ORDER} Hi NarkOnline! 🛒\n\nI just placed an order.\n\nOrder ID: ${orderId}\nTotal: R${total}\n\nPlease confirm my order. Thank you!`,
  
  orderStatus: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.ORDER} Hi NarkOnline! 📦\n\nI'd like to check the status of my order.\n\nOrder ID: ${orderId}\n\nCan you help me?`,
  
  orderIssue: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.ORDER} Hi NarkOnline! ⚠️\n\nI have an issue with my order.\n\nOrder ID: ${orderId}\n\nPlease assist me.`,

  paymentIssue: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.ORDER} Hi NarkOnline! 💳\n\nI have a payment issue with my order.\n\nOrder ID: ${orderId}\n\nPlease help me resolve this.`,

  // ============ REFUNDS [REFUND] ============
  refundRequest: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.REFUND} Hi NarkOnline! 💰\n\nI'd like to request a refund for my order.\n\nOrder ID: ${orderId}\n\nPlease assist.`,

  // ============ DELIVERY COORDINATION [DELIVERY] ============
  deliveryUpdate: (orderId, address) => 
    `${WHATSAPP_CONFIG.TAGS.DELIVERY} Hi! 🚗\n\nI'm your delivery driver from NarkOnline.\n\nOrder ID: ${orderId}\nDelivery Address: ${address}\n\nI'm on my way with your order!`,
  
  deliveryArrived: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.DELIVERY} Hi! 📍\n\nI've arrived with your NarkOnline order.\n\nOrder ID: ${orderId}\n\nPlease come to collect your delivery. Thank you!`,
  
  customerToDriver: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.DELIVERY} Hi! 👋\n\nI'm waiting for my NarkOnline delivery.\n\nOrder ID: ${orderId}\n\nCan you give me an update?`,

  deliveryIssue: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.DELIVERY} Hi NarkOnline! 🚨\n\nI have an issue with my delivery.\n\nOrder ID: ${orderId}\n\nPlease help!`,

  // ============ DRIVER QUERIES [DRIVER] ============
  driverSupport: () => 
    `${WHATSAPP_CONFIG.TAGS.DRIVER} Hi NarkOnline! 🛵\n\nI'm a delivery driver and need assistance.\n\n`,

  driverOrderIssue: (orderId) => 
    `${WHATSAPP_CONFIG.TAGS.DRIVER} Hi NarkOnline! 🛵\n\nI'm a driver having an issue with an order.\n\nOrder ID: ${orderId}\n\nPlease assist.`,
};

/**
 * Quick WhatsApp support link (no button, just a link)
 */
export const WhatsAppLink = ({ 
  phone = SUPPORT_WHATSAPP, 
  message = '', 
  children,
  className = '' 
}) => {
  return (
    <a
      href={getWhatsAppUrl(phone, message)}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 text-green-600 hover:text-green-700 hover:underline ${className}`}
      data-testid="whatsapp-link"
    >
      <MessageCircle className="w-4 h-4" />
      {children || 'Chat on WhatsApp'}
    </a>
  );
};

/**
 * Floating WhatsApp button for bottom-right corner
 */
export const FloatingWhatsAppButton = ({ 
  phone = SUPPORT_WHATSAPP, 
  message = WhatsAppMessages.generalSupport()
}) => {
  const handleClick = () => {
    window.open(getWhatsAppUrl(phone, message), '_blank');
  };

  return (
    <button
      onClick={handleClick}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
      data-testid="floating-whatsapp"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </button>
  );
};

/**
 * WhatsApp contact card with department selection
 */
export const WhatsAppContactCard = ({ className = '' }) => {
  const departments = [
    { label: 'Customer Support', message: WhatsAppMessages.generalSupport(), icon: '💬' },
    { label: 'Orders & Sales', message: WhatsAppMessages.orderStatus(''), icon: '🛒' },
    { label: 'Delivery Help', message: WhatsAppMessages.deliveryIssue(''), icon: '🚗' },
  ];

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border border-stone-200 ${className}`}>
      <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-green-600" />
        Chat with Us
      </h3>
      <div className="space-y-2">
        {departments.map((dept, idx) => (
          <a
            key={dept.label}
            href={getWhatsAppUrl(SUPPORT_WHATSAPP, dept.message)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-green-50 transition-colors"
          >
            <span className="text-xl">{dept.icon}</span>
            <span className="text-sm font-medium text-stone-700">{dept.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

export default {
  getWhatsAppUrl,
  WhatsAppButton,
  WhatsAppMessages,
  WhatsAppLink,
  FloatingWhatsAppButton,
  WhatsAppContactCard,
  SUPPORT_WHATSAPP,
  WHATSAPP_CONFIG
};
